<template>
  <b-row>
    <b-col>
      <b-row class="pb-2">
        <b-col>
          <b-row>
            <b-col cols="auto" class="pr-3">
              <b-checkbox
                class="classification-toggle"
                :checked="active"
                :disabled="alwaysActive"
                @change="onActiveChanged"
                >Ativo
              </b-checkbox>
            </b-col>
            <b-col>
              <div class="float-right">
                <b-button
                  v-if="isConformitaMaster && hasSelection"
                  size="sm"
                  class="mr-2"
                  :class="portalActionClass"
                  :variant="portalActionVariant"
                  :disabled="isPortalUpdating"
                  @click="onPortalVisibilityActionClicked"
                >
                  {{ portalActionLabel }}
                </b-button>
                <b-button
                  v-if="isConformitaMaster && hasSelection"
                  size="sm"
                  class="mr-2"
                  variant="danger"
                  :disabled="isBulkDeleting"
                  @click="onBulkDeleteClicked"
                >
                  Deletar ({{ selectedCount }})
                </b-button>
                <b-button size="sm" variant="primary" @click="onNewItemClicked">
                  Adicionar Valor
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row class="pb-3">
        <b-col cols="12" lg="8">
          <b-form-group
            label="Nome do campo no portal"
            label-for="portal-field-label"
            description="Deixe vazio para usar o nome padrão de tradução no portal."
            class="mb-0"
          >
            <b-input-group>
              <b-form-input
                id="portal-field-label"
                v-model="portalFieldLabelInput"
                trim
                maxlength="120"
                placeholder="Ex.: Departamento envolvido"
              ></b-form-input>
              <b-input-group-append>
                <b-button
                  variant="outline-primary"
                  :disabled="
                    !hasPortalFieldLabelChanges || isSavingPortalFieldLabel
                  "
                  @click="onPortalFieldLabelSave"
                >
                  Salvar nome
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12" xl="12">
          <div class="table-container">
            <b-table
              ref="table"
              :hover="true"
              :striped="false"
              :bordered="false"
              :small="false"
              :fixed="false"
              responsive="sm"
              :items="retrieveData"
              :fields="tableHeaders"
              thead-class="classification-header"
              tbody-tr-class="classification-row"
              borderless
              show-empty
            >
              <template #head(select)>
                <div
                  class="d-flex justify-content-center"
                  v-if="isConformitaMaster"
                >
                  <b-form-checkbox
                    :checked="allVisibleSelected"
                    :indeterminate="hasPartialSelection"
                    aria-label="Selecionar todos os itens"
                    @change="toggleSelectAll"
                  />
                </div>
              </template>
              <template #cell(select)="data">
                <div
                  class="d-flex justify-content-center"
                  v-if="isConformitaMaster"
                >
                  <b-form-checkbox
                    :checked="isSelected(data.item._id)"
                    :aria-label="`Selecionar ${data.item.value}`"
                    @change="onItemSelectionChanged(data.item, $event)"
                  />
                </div>
              </template>
              <template #cell(value)="data">
                <div class="d-flex align-items-center">
                  <span class="value-text">{{ data.item.value }}</span>
                  <b-badge
                    v-if="data.item.portalEnabled"
                    variant="info"
                    class="ml-2 portal-badge"
                  >
                    Portal
                  </b-badge>
                </div>
              </template>
              <template #cell(actions)="data">
                <b-button
                  size="sm"
                  class="mr-1 action-btn"
                  variant="link"
                  title="Editar"
                  @click.stop="edit(data.item, data.index, $event.target)"
                >
                  <i class="fa fa-edit action-icon" aria-hidden="true"></i>
                </b-button>
              </template>
              <template #empty>
                <div class="text-muted text-center py-4">
                  <i class="fas fa-inbox fa-2x"></i>
                  <p class="mt-3">Nenhum valor cadastrado</p>
                </div>
              </template>
            </b-table>
          </div>
        </b-col>
      </b-row>
      <!-- Modal Component -->
      <b-modal
        v-model="modal.visible"
        :title="modal.title"
        ok-title="Salvar"
        cancel-title="Cancelar"
        :ok-disabled="!validateState('value')"
        @ok="onModalSave"
      >
        <b-form-group label="Valor">
          <b-form-input
            v-model="modal.value"
            v-validate="'required'"
            type="text"
            class="form-control"
            aria-describedby="invalidValue"
            name="value"
            :state="validateState('value')"
          ></b-form-input>
          <b-form-invalid-feedback id="invalidValue">
            {{ errors.first("value") }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-modal>
      <b-modal
        v-model="deleteModal.visible"
        title="Remover"
        ok-title="Substituir e apagar"
        cancel-title="Cancelar"
        :ok-disabled="!validateState('replaceOption')"
        @ok="onReplace"
      >
        Existem registros utilizando a classificação
        <strong>{{ deleteModal.value }}</strong
        >.<br />
        Deseja substituir por outra classificação?
        <b-form-group>
          <b-form-select
            v-model="deleteModal.selected"
            v-validate="'required'"
            class="form-control"
            :options="otherOptions"
            aria-describedby="invalidReplace"
            name="replaceOption"
          ></b-form-select>
          <b-form-invalid-feedback id="invalidReplace">
            {{ errors.first("replaceOption") }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-modal>
    </b-col>
  </b-row>
</template>
<script>
import {
  getClassificationValues,
  editClassification,
  newClassification,
  updateClassificationParameter,
  deleteClassification,
  replaceClassification,
  setClassificationPortalVisibility,
} from "@/api/company";
import { mapGetters } from "vuex";

function normalizePortalFieldLabel(label) {
  if (label === undefined || label === null) {
    return null;
  }

  const text = String(label).trim();
  return text.length ? text : null;
}

export default {
  name: "ClassificationItems",
  props: {
    company: {
      type: [Number, String],
      required: true,
    },
    group: {
      type: [Number, String],
      required: false,
      default: null,
    },
    type: {
      type: Number,
      required: true,
    },
    active: Boolean,
    alwaysActive: {
      type: Boolean,
      default: false,
    },
    portalEnabled: Boolean,
    portalFieldLabel: {
      type: String,
      default: null,
    },
  },
  data() {
    const initialPortalFieldLabel = normalizePortalFieldLabel(
      this.portalFieldLabel
    );

    return {
      modal: {
        visible: false,
        id: null,
        value: null,
      },
      deleteModal: {
        visible: false,
        id: null,
        value: null,
        selected: null,
      },
      options: [],
      tableItems: [],
      selectedIds: [],
      isBulkDeleting: false,
      isPortalUpdating: false,
      isSavingPortalFieldLabel: false,
      portalFieldLabelInput: initialPortalFieldLabel || "",
      savedPortalFieldLabel: initialPortalFieldLabel,
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    isConformitaMaster() {
      return ["root", "company-root"].includes(this.profile.role);
    },
    otherOptions() {
      if (this.deleteModal.id) {
        return this.options.filter((opt) => opt.value !== this.deleteModal.id);
      }
      return [];
    },
    tableHeaders() {
      const fields = [{ key: "value", label: "Valor" }];

      if (this.isConformitaMaster) {
        fields.unshift({
          key: "select",
          label: "",
          thClass: "select-column",
          tdClass: "select-column",
        });
      }

      fields.push({
        key: "actions",
        label: "Ações",
        thClass: "actions-column-header",
        tdClass: "actions-column-cell",
      });

      return fields;
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    hasSelection() {
      return this.selectedCount > 0;
    },
    allVisibleSelected() {
      if (!this.tableItems.length) {
        return false;
      }
      return this.tableItems.every((item) =>
        this.selectedIds.includes(item._id)
      );
    },
    hasPartialSelection() {
      return this.selectedCount > 0 && !this.allVisibleSelected;
    },
    selectedItems() {
      if (!this.selectedIds.length) {
        return [];
      }
      return this.tableItems.filter((item) =>
        this.selectedIds.includes(item._id)
      );
    },
    allSelectedVisibleOnPortal() {
      if (!this.selectedItems.length) {
        return false;
      }
      return this.selectedItems.every((item) => item.portalEnabled);
    },
    portalActionLabel() {
      return this.allSelectedVisibleOnPortal
        ? "Tirar do portal"
        : "Visível no portal";
    },
    portalActionVariant() {
      return this.allSelectedVisibleOnPortal
        ? "outline-secondary"
        : "outline-primary";
    },
    portalActionClass() {
      return this.allSelectedVisibleOnPortal ? "portal-remove-btn" : "";
    },
    hasPortalFieldLabelChanges() {
      return (
        normalizePortalFieldLabel(this.portalFieldLabelInput) !==
        this.savedPortalFieldLabel
      );
    },
  },
  methods: {
    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },
    edit(item) {
      this.modal.title = "Editar";
      this.modal.id = item._id;
      this.modal.value = item.value;
      this.modal.visible = true;
    },

    async remove(item) {
      try {
        await deleteClassification(this.company, item._id);
        this.$refs.table.refresh();
      } catch (e) {
        if (e.response.status === 412) {
          this.deleteModal.id = item._id;
          this.deleteModal.value = item.value;
          this.deleteModal.selected = null;
          this.deleteModal.visible = true;
        } else {
          this.$bvToast.toast(e.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },

    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    onItemSelectionChanged(item, checked) {
      if (!item || !item._id) {
        return;
      }

      if (checked && !this.selectedIds.includes(item._id)) {
        this.selectedIds.push(item._id);
        return;
      }

      if (!checked) {
        this.selectedIds = this.selectedIds.filter((id) => id !== item._id);
      }
    },

    toggleSelectAll(checked) {
      if (!checked) {
        this.selectedIds = [];
        return;
      }

      this.selectedIds = this.tableItems.map((item) => item._id);
    },

    clearSelection() {
      this.selectedIds = [];
    },

    async onBulkDeleteClicked() {
      if (!this.hasSelection || this.isBulkDeleting) {
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Tem certeza que deseja excluir ${this.selectedCount} valor(es) selecionado(s)?`,
        {
          title: "Confirmar exclusão",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Sim, excluir",
          cancelTitle: "Cancelar",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );

      if (!confirmed) {
        return;
      }

      const selectedItems = this.tableItems.filter((item) =>
        this.selectedIds.includes(item._id)
      );

      let successCount = 0;
      const failedWithDependency = [];
      const failedGeneric = [];

      this.isBulkDeleting = true;
      try {
        for (const item of selectedItems) {
          try {
            await deleteClassification(this.company, item._id);
            successCount += 1;
          } catch (error) {
            if (error.response && error.response.status === 412) {
              failedWithDependency.push(item.value);
            } else {
              failedGeneric.push(item.value);
            }
          }
        }
      } finally {
        this.isBulkDeleting = false;
      }

      this.clearSelection();
      this.$refs.table.refresh();

      if (successCount > 0) {
        this.$bvToast.toast(
          `${successCount} valor(es) excluído(s) com sucesso!`,
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
      }

      if (failedWithDependency.length || failedGeneric.length) {
        const failedPreview = [...failedWithDependency, ...failedGeneric]
          .slice(0, 3)
          .join(", ");
        const remaining =
          failedWithDependency.length + failedGeneric.length - 3;
        const suffix = remaining > 0 ? ` e mais ${remaining}` : "";

        this.$bvToast.toast(
          `Não foi possível excluir ${
            failedWithDependency.length + failedGeneric.length
          } valor(es). ${failedPreview}${suffix}.`,
          {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 9000,
            noCloseButton: false,
          }
        );
      }
    },

    async onPortalVisibilityActionClicked() {
      if (!this.hasSelection || this.isPortalUpdating) {
        return;
      }

      const selectedItems = this.selectedItems;
      const targetPortalVisibility = !this.allSelectedVisibleOnPortal;
      const pendingItems = selectedItems.filter(
        (item) => item.portalEnabled !== targetPortalVisibility
      );

      if (!pendingItems.length) {
        const message = this.allSelectedVisibleOnPortal
          ? "Os valores selecionados já estão ocultos no portal."
          : "Os valores selecionados já estão visíveis no portal.";
        this.$bvToast.toast(message, {
          title: "Atenção",
          toastClass: "custom-toast custom-toast-warning",
          variant: "light",
          solid: true,
          autoHideDelay: 6000,
          noCloseButton: false,
        });
        this.clearSelection();
        return;
      }

      this.isPortalUpdating = true;
      let successCount = 0;
      const failedItems = [];

      try {
        for (const item of pendingItems) {
          try {
            await setClassificationPortalVisibility(
              this.company,
              item._id,
              targetPortalVisibility
            );
            successCount += 1;
          } catch (error) {
            failedItems.push(item.value);
          }
        }
      } finally {
        this.isPortalUpdating = false;
      }

      if (successCount > 0) {
        const successMessage = targetPortalVisibility
          ? `${successCount} valor(es) marcado(s) como visível no portal.`
          : `${successCount} valor(es) removido(s) do portal.`;
        this.$bvToast.toast(successMessage, {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      }

      if (failedItems.length) {
        const preview = failedItems.slice(0, 3).join(", ");
        const remaining = failedItems.length - 3;
        const suffix = remaining > 0 ? ` e mais ${remaining}` : "";
        this.$bvToast.toast(
          `Não foi possível atualizar ${failedItems.length} valor(es): ${preview}${suffix}.`,
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 9000,
            noCloseButton: false,
          }
        );
      }

      this.clearSelection();
      this.$refs.table.refresh();
    },

    async onReplace() {
      try {
        if (this.deleteModal.id && this.deleteModal.selected) {
          await replaceClassification(
            this.company,
            this.deleteModal.id,
            this.deleteModal.selected
          );
          await deleteClassification(this.company, this.deleteModal.id);
          this.$bvToast.toast("Opção editada com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast("Não foi possível concluir a operação!", {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 8000,
            noCloseButton: false,
          });
        }
        this.$refs.table.refresh();
      } catch (error) {
        console.log(error);
        if (error.response.status === 403) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },

    async onModalSave() {
      try {
        if (this.modal.id) {
          await editClassification(this.company, this.modal.id, {
            value: this.modal.value,
          });
          this.$bvToast.toast("Opção editada com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        } else {
          await newClassification(this.company, {
            type: this.type,
            value: this.modal.value,
            group: this.group,
          });
          this.$bvToast.toast("Opção criada com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        }
        this.$refs.table.refresh();
      } catch (error) {
        if (error.response.status === 403) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },

    async onPortalFieldLabelSave() {
      if (this.isSavingPortalFieldLabel || !this.hasPortalFieldLabelChanges) {
        return;
      }

      const portalFieldLabel = normalizePortalFieldLabel(
        this.portalFieldLabelInput
      );

      this.isSavingPortalFieldLabel = true;
      try {
        await updateClassificationParameter(this.company, this.type, {
          group: this.group,
          portalFieldLabel,
        });

        this.savedPortalFieldLabel = portalFieldLabel;
        this.portalFieldLabelInput = portalFieldLabel || "";

        this.$bvToast.toast("Nome do campo no portal salvo com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        const status = error?.response?.status;
        const message =
          status === 403
            ? error.response.data
            : error?.response?.statusText ||
              "Não foi possível salvar o nome do campo.";

        this.$bvToast.toast(message, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.isSavingPortalFieldLabel = false;
      }
    },

    onNewItemClicked() {
      this.modal.title = "Adicionar";
      this.modal.id = null;
      this.modal.value = null;
      this.modal.visible = true;
    },

    async retrieveData() {
      const response = await getClassificationValues(
        this.company,
        this.type,
        this.group
      );
      this.tableItems = response.data;
      this.options = response.data.map((opt) => ({
        value: opt._id,
        text: opt.value,
      }));
      const validIds = new Set(this.tableItems.map((item) => item._id));
      this.selectedIds = this.selectedIds.filter((id) => validIds.has(id));
      return response.data;
    },

    async onActiveChanged(active) {
      try {
        await updateClassificationParameter(this.company, this.type, {
          group: this.group,
          active,
        });
        const text = active
          ? "Parâmetro ativado com sucesso!"
          : "Parâmetro desativado com sucesso!";
        this.$bvToast.toast(text, {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } catch (error) {
        if (error.response.status === 403) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },
  },
  watch: {
    portalFieldLabel(newValue) {
      const normalized = normalizePortalFieldLabel(newValue);
      this.savedPortalFieldLabel = normalized;
      this.portalFieldLabelInput = normalized || "";
    },
    type() {
      this.clearSelection();
    },
    group() {
      this.clearSelection();
    },
    company() {
      this.clearSelection();
    },
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
  margin-top: 1rem;
}

::v-deep .classification-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
  padding: 0.85rem 0.75rem;
}

::v-deep .classification-row {
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

::v-deep .classification-row:hover {
  background-color: #f8f9fa !important;
}

::v-deep tbody tr:last-of-type {
  border-bottom: none;
}

::v-deep .classification-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

::v-deep .classification-header .select-column,
::v-deep .classification-row .select-column {
  width: 3.25rem;
  text-align: center;
}

::v-deep .classification-header .actions-column-header,
::v-deep .classification-row .actions-column-cell {
  width: 6rem;
  text-align: right;
  white-space: nowrap;
}

::v-deep .value-text {
  color: #495057;
  font-weight: 500;
}

::v-deep .portal-badge {
  background-color: #6fceea66;
  color: #2f6f86;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-radius: 30px;
  padding: 0.25rem 0.6rem;
}

::v-deep .action-btn {
  padding: 0.25rem 0.5rem;
}

::v-deep .action-icon {
  color: #6c757d;
  transition: color 0.2s;
  font-size: 0.95rem;
}

::v-deep .action-btn:hover .action-icon {
  color: #007bff;
}

::v-deep .classification-toggle .custom-control-label {
  white-space: nowrap;
}

::v-deep .portal-remove-btn,
::v-deep .portal-remove-btn:hover,
::v-deep .portal-remove-btn:focus,
::v-deep .portal-remove-btn:active {
  color: #5d5d5d !important;
}
</style>
